import type { AssetType } from "schemas/generated/asset";

import * as React from "react";

import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { assetClient, assetRouter } from "@v2/trpc/routes/asset";
import { uploadToCloudflare } from "@v2/utils/cloudflareUpload";
import DOMPurify from "dompurify";
import { slugify } from "replo-utils/lib/string";
import { ReploError, ReploValidationError } from "schemas/errors";
import { v4 } from "uuid";

export class MediaDimensionError extends ReploError {}
export class SvgDimensionError extends ReploValidationError {}

export const useUploadAsset = () => {
  const [isUploading, setIsUploading] = React.useState(false);
  const [isError, setIsError] = React.useState(false);
  const projectId = useCurrentProjectId();
  const { mutateAsync: createAsset } = assetRouter.create.useMutation({
    onSuccess: () => {
      setIsUploading(false);
    },
    onError: (_, input) => {
      setIsError(true);

      // NOTE (Gabe 2025-02-27): This ensures we delete the asset from
      // cloudflare if we fail to create it in our database.
      const asset = input.asset;
      void assetClient.delete.mutate({
        projectId: asset.projectId,
        assetId: asset.id,
        type: asset.type,
      });
    },
    onSettled: () => {
      setIsUploading(false);
    },
  });

  const uploadAsset = React.useCallback(
    async ({
      assetId,
      file,
    }: {
      assetId: string;
      file: File;
    }): Promise<string> => {
      if (!projectId) {
        // eslint-disable-next-line replo/no-raw-error-throw
        throw new Error("Project ID is required to upload assets.");
      }
      const assetName = slugify(file.name.split(".")[0] ?? ""); // Remove file extension
      const assetType = getAssetTypeFromFile(file);
      const fileExtension = file.name.split(".").at(-1) ?? "";

      // NOTE (Nick, 2025-11-05): Sanitize SVG files before uploading
      // NOTE (Nick, 2026-01-15, REPL-22220): Validate SVG dimensions to prevent Sharp errors
      let fileToUpload = file;
      if (isSvgFile(file)) {
        fileToUpload = await sanitizeSvgFile(file);
        const svgContent = await fileToUpload.text();
        validateSvgDimensions(svgContent);

        fileToUpload = new File([svgContent], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        });
      }

      setIsUploading(true);

      const assetUrl = await uploadToCloudflare({
        file: fileToUpload,
        // TODO (Gabe 2025-02-26): Not great that we have this assetId=>filename
        // mapping here and elsewhere.
        filename: assetId,
        projectId,
        fetchSignedUrl: (params) => assetClient.fetchSignedUrl.query(params),
        errorContext: "image",
      });

      const now = new Date();
      // NOTE (Kevin, 2025-12-23, REPL-21472): Dimension extraction is best-effort.
      // If it fails (e.g., browser can't decode the image), we still want the
      // upload to succeed since the asset is already in Cloudflare.
      let width: number | null = null;
      let height: number | null = null;
      try {
        const dimensions = await getMediaDimensions(file);
        width = dimensions.width;
        height = dimensions.height;
      } catch (error) {
        // NOTE (Kevin, 2025-12-30, REPL-21472): Silently ignore dimension extraction
        // failures since we already uploaded the asset to Cloudflare.
        if (!(error instanceof MediaDimensionError)) {
          throw error;
        }
      }

      try {
        await createAsset({
          asset: {
            id: assetId,
            url: assetUrl,
            name: assetName,
            type: assetType,
            sizeBytes: file.size,
            fileExtension,
            width,
            height,
            projectId,
            createdAt: now,
            updatedAt: now,
            altText: null,
            embeddingText: null,
          },
        });
      } catch (error) {
        void assetClient.delete.mutate({
          projectId,
          assetId: assetId,
          type: assetType,
        });
        throw error;
      }

      return assetUrl;
    },
    [createAsset, projectId],
  );

  return { uploadAsset, isUploading, isError };
};

export const batchUploadToCloudflare = async (
  files: File[],
  projectId: string,
): Promise<{ file: File; assetUrl: string }[]> => {
  const uploadPromises = files.map(async (file) => {
    const assetUrl = await uploadToCloudflare({
      file,
      filename: `chat-assets-${v4()}`,
      projectId,
      fetchSignedUrl: (params) => assetClient.fetchSignedUrl.query(params),
      errorContext: "image",
    });

    return {
      file,
      assetUrl,
    };
  });

  return Promise.all(uploadPromises);
};

async function getMediaDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  const fileType = file.type.split("/")[0];

  if (fileType === "image") {
    return await getImageDimensions(file);
  } else if (fileType === "video") {
    return await getVideoDimensions(file);
  } else {
    throw new MediaDimensionError({
      message: "Unsupported file type",
      additionalData: { fileType, fileName: file.name, mimeType: file.type },
    });
  }
}

export function getImageDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    // NOTE (Kevin, 2025-12-15): Use createObjectURL instead of readAsDataURL.
    // Data URLs can fail for large files.
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.addEventListener("load", () => {
      URL.revokeObjectURL(objectUrl);
      // NOTE (Kevin, 2025-12-23, REPL-21472): Use img.decode() when available
      // for more reliable dimension extraction, especially for images that
      // might have metadata issues.
      if (typeof img.decode === "function") {
        img
          .decode()
          .then(() => {
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
          })
          .catch(() => {
            resolve({ width: img.width, height: img.height });
          });
      } else {
        resolve({ width: img.width, height: img.height });
      }
    });

    img.addEventListener("error", () => {
      URL.revokeObjectURL(objectUrl);
      reject(
        new MediaDimensionError({
          message: "Failed to load image",
          additionalData: { fileName: file.name, mimeType: file.type },
        }),
      );
    });

    img.src = objectUrl;
  });
}

export function getVideoDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    // NOTE (Kevin, 2025-12-15): Use createObjectURL instead of readAsDataURL.
    // Data URLs can fail for large files (e.g., .mov videos).
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";

    video.addEventListener("loadedmetadata", () => {
      URL.revokeObjectURL(objectUrl);
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        resolve({ width: video.videoWidth, height: video.videoHeight });
      } else {
        reject(
          new MediaDimensionError({
            message: "Failed to extract video dimensions",
            additionalData: {
              fileName: file.name,
              mimeType: file.type,
              videoWidth: video.videoWidth,
              videoHeight: video.videoHeight,
            },
          }),
        );
      }
    });

    video.addEventListener("error", () => {
      URL.revokeObjectURL(objectUrl);
      reject(
        new MediaDimensionError({
          message: "Failed to load video",
          additionalData: { fileName: file.name, mimeType: file.type },
        }),
      );
    });

    video.src = objectUrl;
  });
}
// TODO (Gabe 2025-02-26): Add support for fonts.

export function getAssetTypeFromFile(
  file: Blob,
): Extract<AssetType, "image" | "video"> {
  const type = file.type.split("/")[0];

  if (type === "image") {
    return "image";
  } else if (type === "video") {
    return "video";
  } else {
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error("Unsupported file type");
  }
}

/**
 * Check if a file is an SVG based on its type or extension
 */
function isSvgFile(file: File): boolean {
  const fileExtension = file.name.split(".").at(-1)?.toLowerCase();
  return file.type === "image/svg+xml" || fileExtension === "svg";
}

/**
 * Note (Nick, 11/5/2025): Use DOMPurify to sanitize an SVG file with secure defaults to strip scripts, event handlers, and other XSS vectors
 */
async function sanitizeSvgFile(file: File): Promise<File> {
  const svgContent = await file.text();

  /**
   * Removes <script> tags
   * Removes event handlers (onload, onclick, etc.)
   * Removes javascript: URLs
   * Removes data: URLs that could contain scripts
   */
  const sanitizedSvg = DOMPurify.sanitize(svgContent, {
    USE_PROFILES: { svg: true, svgFilters: true },
  });

  return new File([sanitizedSvg], file.name, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

/**
 * Note (Nick, 2026-01-15, REPL-22220): Validates that an SVG has explicit dimensions (width/height or viewBox).
 * SVGs without dimensions cause Sharp to fail with "svgload_buffer: bad dimensions" when
 * the chat agent tries to rasterize them.
 */
export function validateSvgDimensions(svgContent: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, "image/svg+xml");
  const svg = doc.querySelector("svg");

  const width = svg?.getAttribute("width");
  const height = svg?.getAttribute("height");
  const viewBox = svg?.getAttribute("viewBox");

  const hasAbsoluteWidth = width && /^\d+(\.\d+)?(px)?$/.test(width);
  const hasAbsoluteHeight = height && /^\d+(\.\d+)?(px)?$/.test(height);
  const hasViewBox = Boolean(viewBox?.trim());

  if (!(hasAbsoluteWidth && hasAbsoluteHeight) && !hasViewBox) {
    throw new SvgDimensionError(
      "SVG must have width and height attributes or a viewBox",
    );
  }
}
